import styled from "@emotion/styled";
import { Box } from "@mui/material";
import {
    Document24Icon,
    Download24Icon,
    Excel24Icon,
    File24Icon,
    Image24Icon,
    Pdf24Icon,
    Play24Icon,
    Slide24Icon,
    Sound24Icon,
    ZipFile24Icon,
} from "@kaltura/ds-react-icons";
import { Tooltip, Typography } from "@kaltura/ds-react-components";
import { KmsTypeMediaAttachment } from "./KmsTypeMediaAttachment";
import { humanReadableSize, getKmsData, baseUrl } from "@kaltura/mediaspace-shared-utils";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import clsx from "clsx";
import { ReactNode } from "react";

export interface MediaAttachmentProps extends KmsTypeMediaAttachment {
    className?: string;
}

const iconBoxSize = 48;

// region Styled components
const StyledContainer = styled("a")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    backgroundColor: theme.kaltura.palette.surfaces.paper,
    borderRadius: theme.kaltura.shape.roundness2,
    overflow: "hidden",
    color: "inherit",
    textDecoration: "none",
}));

const StyledTypeIconBox = styled(Box)(({ theme }) => ({
    display: "flex",
    width: iconBoxSize,
    height: iconBoxSize,
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(2),
    backgroundColor: theme.kaltura.palette.tone6,
}));

const StyledFileName = styled(Typography)({
    // Low width with flex-grow is required for a full-width flex item with ellipsis
    width: 1,
    flexGrow: 1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
});

const StyledDownloadIconBox = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 2),
}));
// endregion

enum AttachmentType {
    Document = "document",
    Pdf = "pdf",
    Data = "data",
    Presentation = "presentation",
    Audio = "audio",
    Image = "image",
    Video = "video",
    Archive = "archive",
    Other = "other",
}

const attachmentTypeMap: Record<string, AttachmentType> = Object.fromEntries(
    Object
        .entries({
            [AttachmentType.Document]: ["doc", "docx", "odt", "rtf", "tex", "txt", "wpd"],
            [AttachmentType.Pdf]: ["pdf"],
            [AttachmentType.Data]: [
                "xls",
                "xlsx",
                "xlsm",
                "ods",
                "csv",
                "dat",
                "db",
                "log",
                "mdb",
                "sav",
                "sql",
                "tar",
                "xml",
            ],
            [AttachmentType.Presentation]: ["key", "odp", "pps", "ppt", "pptx"],
            [AttachmentType.Audio]: ["mp3", "aif", "cda", "mid", "mp3", "mpa", "ogg", "wav", "wma", "wpl"],
            [AttachmentType.Image]: ["ai", "jpg", "bmp", "gif", "ico", "jpeg", "png", "ps", "psd", "svg", "tif"],
            [AttachmentType.Video]: [
                "3g2",
                "3gp",
                "avi",
                "flv",
                "h264",
                "m4v",
                "mkv",
                "mov",
                "mp4",
                "mpg",
                "rm",
                "swf",
                "vob",
                "wmv",
            ],
            [AttachmentType.Archive]: ["7z", "arj", "deb", "pkg", "rar", "rpm", "tar", "gz", "z", "zip"],
        })
        .flatMap(([type, extensions]) => extensions.map((extension) => [extension, type as AttachmentType]))
);

// Note: can't use the enum for key values because AttachmentResponse can be imported only as a type
const iconsMap: Record<AttachmentType, ReactNode> = {
    document: <Document24Icon />,
    pdf: <Pdf24Icon />,
    data: <Excel24Icon />,
    presentation: <Slide24Icon />,
    audio: <Sound24Icon />,
    image: <Image24Icon />,
    video: <Play24Icon />,
    archive: <ZipFile24Icon />,
    other: <File24Icon />,
};

/**
 * Media attachment item
 */
export const MediaAttachment = ({
    className: classNameProp = "",
    title,
    description,
    fileExt,
    fileName,
    size,
    downloadUrl,
    entryId,
    attachmentId,
}: MediaAttachmentProps) => {
    const className = "kms-ds-media-page-moduledata-attachment";

    const sendButtonAnalytics = useButtonAnalytics();

    const sendAnalytics = async () => {
        // send beacon
        sendButtonAnalytics("Download media attachment", ButtonClickAnalyticsType.DOWNLOAD);

        // send analytics
        await getKmsData(
            baseUrl
                + "/attachments/index/send-download-analytics-event/entryId/"
                + entryId
                + "/attachmentId/"
                + attachmentId
                + "?format=ajax"
        );
    };

    return (
        <Tooltip
            title={
                <>
                    <div>{title}</div>
                    <div>{description}</div>
                    <div>{fileName}</div>
                    <div>{humanReadableSize(size)}</div>
                </>
            }
            placement={"top"}
            disableTouchListener={true}
        >
            <StyledContainer
                className={clsx(className, classNameProp)}
                href={downloadUrl}
                download={fileName}
                onClick={sendAnalytics}
                target={"_blank"}
            >
                <StyledTypeIconBox className={className + "-icon"}>
                    {iconsMap[attachmentTypeMap[fileExt?.toLowerCase()] ?? AttachmentType.Other]}
                </StyledTypeIconBox>
                <StyledFileName variant={"body2Highlight"} className={className + "-filename"}>
                    {title ? title : fileName}
                </StyledFileName>
                <StyledDownloadIconBox className={className + "-download"}>
                    <Download24Icon />
                </StyledDownloadIconBox>
            </StyledContainer>
        </Tooltip>
    );
};
